import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { FormLoadingState } from '../../components/ui/FormLoadingState';
import { FormSuccessMessage } from '../../components/ui/FormSuccessMessage';

import { useAccountManagement } from '../../hooks/useAccountManagement';
import { useAccountDeletion } from '../../hooks/useAccountDeletion';
import { useAuth } from '../../hooks/useAuth';
import { UsernameChangeForm } from '../../components/forms/UsernameChangeForm';
import { EmailChangeForm } from '../../components/forms/EmailChangeForm';
import { PasswordChangeForm } from '../../components/forms/PasswordChangeForm';
import { AccountDeletionSection } from '../../components/forms/AccountDeletionSection';
import { AlertCircle, User, Mail, Key, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

export const AccountManagementPage: React.FC = () => {
  const { user, userProfile, loading, error, refreshUser, clearError } = useAccountManagement();
  const { deleteAccount, isSubmitting: isDeletingAccount, error: deletionError, success: deletionSuccess, validationError: deletionValidationError } = useAccountDeletion();
  const { resendVerificationEmail, isEmailVerified } = useAuth();
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  // Note: User data is automatically fetched by useAccountManagement hook
  // No need to manually refresh on mount as it causes unnecessary re-renders

  const handleUsernameSuccess = (newUsername: string) => {
    setSuccessMessage(`Username successfully updated to "${newUsername}"`);
    setShowUsernameForm(false);
    refreshUser(); // Refresh to get updated user data
    
    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleUsernameError = (errorMessage: string) => {
    // Error is handled within the form component
    console.error('Username update error:', errorMessage);
  };

  const handleEmailSuccess = () => {
    setSuccessMessage('Verification email sent! Please check your inbox and click the verification link to complete the email change.');
    // Don't close the form immediately - let user see the verification status in the form

    // Clear success message after 8 seconds (longer because this is important)
    setTimeout(() => setSuccessMessage(null), 8000);
  };

  const handleEmailError = (errorMessage: string) => {
    // Error is handled within the form component
    console.error('Email update error:', errorMessage);
  };

  const handlePasswordSuccess = () => {
    setSuccessMessage('Password successfully updated!');
    setShowPasswordForm(false);
    
    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handlePasswordError = (errorMessage: string) => {
    // Error is handled within the form component
    console.error('Password update error:', errorMessage);
  };

  const handleAccountDeletion = async (password: string, captchaToken: string) => {
    try {
      await deleteAccount(password, captchaToken);
      // If successful, user will be signed out automatically
      // The auth context will handle the redirect
    } catch (error) {
      // Error is already handled by the useAccountDeletion hook
      console.error('Account deletion failed:', error);
    }
  };

  const handleResendVerification = async () => {
    setVerificationError(null);
    setSuccessMessage(null);

    try {
      setResendingVerification(true);
      const { error } = await resendVerificationEmail();

      if (error) {
        setVerificationError(error.message || 'Failed to resend verification email');
      } else {
        setSuccessMessage('Verification email sent! Please check your inbox and click the verification link.');

        // Clear success message after 8 seconds
        setTimeout(() => setSuccessMessage(null), 8000);
      }
    } catch (err: any) {
      setVerificationError(err.message || 'Failed to resend verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  // Redirect to login if not authenticated
  if (!loading && !user) {
    return <Navigate to="/popular" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-4 sm:py-8 lg:py-12 px-4 sm:px-6">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 sm:gap-6 lg:gap-8">
        {/* Header */}
        <div className="space-y-2 sm:space-y-3">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-primary/80">
            Settings · Account
          </p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
            Account Management
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Update your profile details and manage your Wallstreetle account settings.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearError}
                  className="ml-auto text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  Dismiss
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Success Message */}
        {successMessage && (
          <FormSuccessMessage 
            message={successMessage} 
            onDismiss={() => setSuccessMessage(null)}
          />
        )}

        <FormLoadingState isLoading={loading} loadingText="Loading account information...">
          {user && (
            <div className="grid gap-4 sm:gap-6">
              {/* Current Account Information */}
              <Card>
                <CardHeader className="pb-4 sm:pb-6">
                  <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                    <User className="w-4 h-4 sm:w-5 sm:h-5" />
                    Account Information
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Your current account details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                        Email Address
                      </label>
                      <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-md break-all">
                        {user.email}
                      </p>
                      {/* Email Verification Status */}
                      <div className="flex items-center gap-2 mt-2">
                        {isEmailVerified ? (
                          <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-xs font-medium">Email verified</span>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-1.5 text-yellow-600 dark:text-yellow-400">
                              <XCircle className="w-4 h-4" />
                              <span className="text-xs font-medium">Email not verified</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleResendVerification}
                              disabled={resendingVerification}
                              className="h-auto py-1 px-2 text-xs text-primary hover:text-primary/80"
                            >
                              {resendingVerification ? (
                                <>
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                  Sending...
                                </>
                              ) : (
                                'Resend verification email'
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                      {/* Verification Error Display */}
                      {verificationError && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md">
                          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                            <AlertCircle className="w-3 h-3" />
                            <span className="text-xs">{verificationError}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                        Username
                      </label>
                      <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-md">
                        {userProfile?.username || 'Not set'}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Account Created
                    </label>
                    <p className="text-sm text-muted-foreground">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Account Management Sections */}
              <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
                {/* Email Management */}
                <Card>
                  <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                      <Mail className="w-4 h-4" />
                      Email Address
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      Update your email address for account notifications
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {showEmailForm ? (
                      <div className="space-y-3 sm:space-y-4">
                        <EmailChangeForm
                          currentEmail={user.email || ''}
                          onSuccess={handleEmailSuccess}
                          onError={handleEmailError}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowEmailForm(false)}
                          className="w-full mobile-touch-target text-sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full mobile-touch-target text-sm sm:text-base"
                        onClick={() => setShowEmailForm(true)}
                      >
                        Change Email
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Username Management */}
                <Card>
                  <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                      <User className="w-4 h-4" />
                      Username
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      Update your display name and username
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {showUsernameForm ? (
                      <div className="space-y-3 sm:space-y-4">
                        <UsernameChangeForm
                          currentUsername={userProfile?.username || ''}
                          onSuccess={handleUsernameSuccess}
                          onError={handleUsernameError}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowUsernameForm(false)}
                          className="w-full mobile-touch-target text-sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="w-full mobile-touch-target text-sm sm:text-base"
                        onClick={() => setShowUsernameForm(true)}
                      >
                        Change Username
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Password Management */}
                <Card>
                  <CardHeader className="pb-3 sm:pb-4">
                    <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                      <Key className="w-4 h-4" />
                      Password
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      Update your account password for security
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {showPasswordForm ? (
                      <div className="space-y-3 sm:space-y-4">
                        <PasswordChangeForm
                          onSuccess={handlePasswordSuccess}
                          onError={handlePasswordError}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPasswordForm(false)}
                          className="w-full mobile-touch-target text-sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="w-full mobile-touch-target text-sm sm:text-base"
                        onClick={() => setShowPasswordForm(true)}
                      >
                        Change Password
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Account Deletion */}
                <div className="lg:col-span-2">
                  <AccountDeletionSection
                    onDeleteAccount={handleAccountDeletion}
                    isSubmitting={isDeletingAccount}
                    error={deletionError}
                    success={deletionSuccess}
                    validationError={deletionValidationError}
                  />
                </div>
              </div>

              {/* Temporary Profile Modal Access */}
              <Card className="border-dashed border-primary/40 bg-background/80">
                <CardHeader className="pb-3 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg">Quick Access</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    While the full account management is being built, you can use the profile modal for basic updates.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    className="w-full mobile-touch-target text-sm sm:text-base sm:w-auto"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-user-modal-for-checkout'))}
                  >
                    Open Profile Modal
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </FormLoadingState>
      </div>
    </div>
  );
};

export default AccountManagementPage;
